import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { AgGridReact } from 'ag-grid-react'
import cn from 'classnames'
import _debounce from 'lodash/debounce'
import Select from 'react-select'

import ColumnsSelectModal from './ColumnsSelectModal'
import { WQGrid } from './WQGrid'
import { allStates, subStageMap } from './WQGrid/CellRenderers/subStageMap'
import { WQHeader } from './WQHeader'
import { useAppSelector } from '@/client/hooks'
import { OptionWithAvatar } from '@/components/ReactSelectCustomControls'
import { PresetsMenu } from '@/features/WorkQueues/PresetsMenu'
import { useGetLoggedUserQuery, useGetStaffUsersQuery } from '@/services/user'
import {
  getUserFullNameOrEmail,
  isSalesOnlyUser,
  staffUserGroupsAvailableInWorkviews,
} from '@/utils/user'

export function WorkQueues() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [visibleColCount, setVisibleColCount] = useState(0)
  const [totalDeals, setTotalDeals] = useState(null)
  const [investmentPaymentAggregate, setInvestmentPaymentAggregate] = useState(null)
  const [showDealsWithNoDealTeam, setShowDealsWithNoDealTeam] = useState(false)
  const gridRef = useRef<AgGridReact>(null)
  const { data: staffUsers, isLoading: isLoadingStaffUsers } = useGetStaffUsersQuery()
  const fileNameRef = useRef(null)
  const dealTeamUserRef = useRef(null)
  const applicationStatesRef = useRef(null)
  const { showWorkviewsFilters } = useAppSelector((state) => state.general)
  const { data: loggedUser } = useGetLoggedUserQuery()

  const filtersWrapperStyles = cn({
    'flex flex-wrap gap-2 2xl:gap-4 items-center m-3': showWorkviewsFilters,
    hidden: !showWorkviewsFilters,
  })

  useEffect(() => {
    window.onbeforeunload = () => {
      const columnState = gridRef.current.columnApi?.getColumnState()
      localStorage.setItem('columnState', JSON.stringify(columnState))
    }
    return () => {
      window.onbeforeunload = null
    }
  }, [])

  const staffUsersOptions = useMemo(
    () =>
      staffUsers
        ?.filter((user) => user?.is_active)
        .filter((user) =>
          user.groups.some((group) => staffUserGroupsAvailableInWorkviews.includes(group.name))
        )
        .map((user) => ({
          label: getUserFullNameOrEmail(user),
          value: user.id,
          user, // required for the avatar
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [staffUsers, isLoadingStaffUsers]
  )

  const subStageStatesOptions = useMemo(() => {
    const options = allStates.map((state) => ({
      label: subStageMap[state].label,
      value: state,
    }))

    if (isSalesOnlyUser(loggedUser)) {
      return options.filter((option) => option.value !== 'pending-qualification')
    }

    return options
  }, [loggedUser])

  const handleSetAdminFilters = (ids) => {
    if (ids?.length)
      dealTeamUserRef?.current?.setValue(
        staffUsersOptions.filter((option) => ids.includes(option.value.toString()))
      )
  }

  const handleSetAplicationStates = (states) => {
    if (states?.length)
      applicationStatesRef?.current?.setValue(
        subStageStatesOptions.filter((option) => states.includes(option.value))
      )
  }

  return (
    <div className='flex flex-col h-screen'>
      <WQHeader />

      <div className={filtersWrapperStyles}>
        <input
          type='search'
          name='fileName'
          ref={fileNameRef}
          placeholder='Search by deal name, phone, email or Agreement Number'
          data-test='search-by-file-name'
          className='h-[38px] border border-gray-300 rounded w-[360px] text-sm'
          onChange={_debounce((event) => {
            const currentFilters = gridRef.current.api.getFilterModel()

            gridRef.current.api.setFilterModel({
              ...currentFilters,
              name: {
                filter: event.target.value,
                filterType: 'text',
                type: 'contains',
              },
            })
          }, 500)}
        />

        <Select
          ref={dealTeamUserRef}
          isMulti
          name='dealTeamUser'
          options={staffUsersOptions}
          isClearable
          closeMenuOnSelect={false}
          placeholder='Filter by Deal Team'
          isLoading={isLoadingStaffUsers}
          className='text-sm min-w-[320px]'
          onChange={_debounce((selectedOptions) => {
            if (!selectedOptions.length) return
            const currentFilters = gridRef.current.api.getFilterModel()
            gridRef.current.api.setFilterModel({
              ...currentFilters,
              deal_teams: {
                filter: selectedOptions?.map((option) => option.value),
                filterType: 'text',
                type: 'in',
              },
            })
            setShowDealsWithNoDealTeam(false)
          }, 1000)}
          components={{ Option: OptionWithAvatar }}
        />

        <label className='min-w-max'>
          <input
            type='checkbox'
            className='checkbox'
            name='showDealsWithNoDealTeam'
            data-test='showDealsWithNoDealTeam'
            checked={showDealsWithNoDealTeam}
            onChange={(event) => {
              setShowDealsWithNoDealTeam(event.target.checked)
              const currentFilters = gridRef.current.api.getFilterModel()
              if (!event.target.checked) {
                gridRef.current.api.destroyFilter('deal_teams')
              } else {
                dealTeamUserRef.current.clearValue()
                gridRef.current.api.setFilterModel({
                  ...currentFilters,
                  deal_teams: { filterType: 'text', type: 'blank' },
                })
              }
            }}
          />
          <span className='ml-2 text-sm'>Show un-assigned deals</span>
        </label>

        <Select
          ref={applicationStatesRef}
          isMulti
          name='applicationStates'
          className='text-sm'
          placeholder='Filter by Sub-stages'
          isClearable
          closeMenuOnSelect={false}
          options={subStageStatesOptions}
          onChange={_debounce((selectedOptions) => {
            const currentFilters = gridRef.current.api.getFilterModel()
            gridRef.current.api.setFilterModel({
              ...currentFilters,
              application_state: {
                filter: selectedOptions?.map((option) => option.value),
                filterType: 'text',
                type: 'in',
              },
            })
          }, 2000)}
        />

        <div className='min-w-max'>
          <button
            className='bg-blue-900 text-white rounded px-1 py-1'
            data-test='visible-columns-button'
            onClick={() => setIsModalOpen(true)}
          >
            {visibleColCount} Columns
          </button>
        </div>

        <button
          onClick={useCallback(() => {
            const allColumnIds: string[] = []
            gridRef.current.columnApi.getColumns().forEach((column) => {
              allColumnIds.push(column.getId())
            })
            gridRef.current.columnApi.autoSizeColumns(allColumnIds, false)
          }, [])}
          className='bg-blue-900 text-white rounded px-1 py-1'
        >
          <ArrowsPointingOutIcon
            width={22}
            height={22}
          />
        </button>

        <PresetsMenu
          gridRef={gridRef}
          handleSetAdminFilters={handleSetAdminFilters}
          handleSetAplicationStates={handleSetAplicationStates}
        />
        <ColumnsSelectModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          gridRef={gridRef}
        />

        <button
          className='text-blue-700 hover:underline'
          data-test='reset-button'
          onClick={() => {
            for (const filter in gridRef.current.api.getFilterModel()) {
              gridRef.current.api.destroyFilter(filter)
            }
            gridRef.current.columnApi.resetColumnState()
            fileNameRef.current.value = ''
            dealTeamUserRef.current.clearValue()
            setShowDealsWithNoDealTeam(false)
            applicationStatesRef.current.clearValue()
          }}
        >
          Reset
        </button>

        <button
          className='text-blue-700 hover:underline'
          data-test='refresh-button'
          onClick={() => gridRef.current.api.refreshInfiniteCache()}
        >
          Refresh
        </button>
      </div>

      <div className='text-sm text-blue-800 border-t border-gray-300 px-4 py-1'>
        <span>Results: {totalDeals}</span>
        {investmentPaymentAggregate && (
          <>
            <span className='mx-3'>|</span>
            <span>
              Investment Payment (aggregate):{' '}
              {Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                investmentPaymentAggregate
              )}
            </span>
          </>
        )}
      </div>

      <WQGrid
        gridRef={gridRef}
        setVisibleColCount={setVisibleColCount}
        setTotalDeals={setTotalDeals}
        setAggregateByInvesmentPayment={setInvestmentPaymentAggregate}
      />
    </div>
  )
}
